import { lazy, Suspense, Component, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { CORE_ESTIMATOR_WEBSITE, codeFromBrandedPath, isBrandedHost } from './apps/lead-dashboard/lib/links';
import './App.css';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,        // data stays fresh for 30 s
            retry: 1,                 // retry failed queries once
            refetchOnWindowFocus: false,
        },
    },
});

// Set global flag so child apps know they're running inside AppShell
// This prevents duplicate Toaster components - apps check this flag
// and skip rendering their own Toaster when inside AppShell
window.__APPSHELL__ = true;

// =============================================================================
// LAZY LOADING CONFIGURATION
// =============================================================================
// Apps are loaded on-demand using React.lazy() for better performance.
// This means each app's code is only downloaded when the user navigates to it.
// Benefits:
// - Faster initial page load
// - Reduced memory usage during development
// - Better hot reload performance (only reloads the active app)
// =============================================================================

// Pages (eagerly loaded - small and always needed)
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';

// Shell Layout (eagerly loaded - needed for all authenticated routes)
import ShellLayout from './components/ShellLayout';

// Lazy-loaded pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));

// Lazy-loaded public pages (no auth required)
const PublicPageView = lazy(() => import('./apps/html-share/pages/PublicView'));
const ProspectView = lazy(() => import('./apps/lead-dashboard/pages/ProspectView'));
const ProspectPreviewView = lazy(() => import('./apps/lead-dashboard/pages/ProspectPreviewView'));
const ThisIsMePage = lazy(() => import('./apps/lead-dashboard/pages/ThisIsMePage'));
const WatchVideo = lazy(() => import('./apps/video-tracker/pages/WatchVideo'));

// =============================================================================
// LAZY-LOADED APP MODULES
// Each app is loaded only when the user navigates to it
// =============================================================================
const LazyApps = {
    'web-tour': lazy(() => import('./apps/web-tour/WebTourApp')),
    'html-share': lazy(() => import('./apps/html-share/HtmlShareApp')),
    'flow-recorder': lazy(() => import('./apps/flow-recorder/FlowRecorderApp')),
    'bim-xpress': lazy(() => import('./apps/bim-xpress/BIMXPRESSApp')),
    'personal-budget': lazy(() => import('./apps/personal-budget/PersonalBudgetApp')),
    'lead-dashboard': lazy(() => import('./apps/lead-dashboard/LeadDashboardApp')),
    'video-tracker': lazy(() => import('./apps/video-tracker/VideoTrackerApp')),
};

// Eagerly loaded utilities (small, needed for fallback)
import ExternalApp from './apps/external/ExternalApp';
import GenericApp from './apps/generic/GenericApp';

// External apps (iframe-based)
// takeoff-viewer: Angular APS viewer served as a static site at /viewer
// (own repo DNO-76/autodesk-viewer — cannot compile into this React bundle)
const EXTERNAL_APPS = {
    'takeoff-viewer': { name: '3D Takeoff', url: '/viewer/' },
};

// =============================================================================
// LOADING COMPONENT
// Shown while lazy-loaded components are being fetched
// =============================================================================
const AppLoadingFallback = ({ appName = 'app' }) => (
    <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
        <p className="text-slate-500 text-sm">Loading {appName}...</p>
    </div>
);

const PageLoadingFallback = () => (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-500 text-sm">Loading...</p>
        </div>
    </div>
);

// Public pages (prospect links, shared pages and videos) are seen by people outside the
// business: a plain white loader that matches the prospect page's own, rather than AppShell's
const PublicPageLoadingFallback = () => (
    <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
    </div>
);

// =============================================================================
// ERROR BOUNDARY
// Catches render errors in lazy-loaded apps so the shell stays alive
// =============================================================================
class AppErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error(`[AppErrorBoundary] App "${this.props.appId}" crashed:`, error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 p-8">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 text-xl font-bold">!</span>
                    </div>
                    <div className="text-center">
                        <p className="text-slate-800 font-semibold mb-1">
                            {this.props.appId} failed to load
                        </p>
                        <p className="text-slate-500 text-sm mb-4">
                            {this.state.error?.message || 'An unexpected error occurred'}
                        </p>
                        <button
                            className="text-sm text-indigo-600 hover:underline"
                            onClick={() => this.setState({ hasError: false, error: null })}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// =============================================================================
// LAZY APP WRAPPER
// Wraps lazy-loaded apps with Suspense and error boundary
// =============================================================================
const LazyAppRoute = ({ appId }) => {
    const LazyComponent = LazyApps[appId];

    if (!LazyComponent) {
        return <GenericApp />;
    }

    return (
        <AppErrorBoundary appId={appId}>
            <Suspense fallback={<AppLoadingFallback appName={appId} />}>
                <LazyComponent />
            </Suspense>
        </AppErrorBoundary>
    );
};

// =============================================================================
// ROUTE GUARDS
// =============================================================================

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return <PageLoadingFallback />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Admin-only route
const AdminRoute = ({ children }) => {
    const { isAuthenticated, isAdmin, loading } = useAuth();
    if (loading) return <PageLoadingFallback />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (!isAdmin) return <Navigate to="/dashboard" replace />;
    return children;
};

// Public Route - redirect to dashboard if authenticated
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return <PageLoadingFallback />;
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// =============================================================================
// ROUTES
// =============================================================================

// The page shell's title is "Core Estimator", because people outside the business see it
// before any code runs. Public pages (prospect links, shared pages and videos) keep it; the
// rest of the site is AppShell's own signed-in side, so those pages rename the tab once loaded.
const PUBLIC_PAGES = /^\/(prospect|prospect-preview|share|watch)(\/|$)/;

// go.coreestimator.com.au only ever shows prospect pages: /<code> opens that prospect's page
// (the same page and history as /prospect/<code> on control-base.com.au). Every other address
// there goes to the Core Estimator website, so AppShell is never shown on that domain.
function BrandedProspectRoutes() {
    const { pathname, search } = useLocation();
    if (pathname === '/me') {
        return (
            <Suspense fallback={<PublicPageLoadingFallback />}>
                <ThisIsMePage />
            </Suspense>
        );
    }
    const code = codeFromBrandedPath(pathname);
    if (!code) return <ToCoreEstimatorWebsite />;
    if (pathname !== `/${code}`) return <Navigate to={`/${code}${search}`} replace />;
    return (
        <Routes>
            <Route
                path="/:slug"
                element={
                    <Suspense fallback={<PublicPageLoadingFallback />}>
                        <ProspectView />
                    </Suspense>
                }
            />
        </Routes>
    );
}

function ToCoreEstimatorWebsite() {
    useEffect(() => {
        window.location.replace(CORE_ESTIMATOR_WEBSITE);
    }, []);
    return <PublicPageLoadingFallback />;
}

function AppRoutes() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (!PUBLIC_PAGES.test(pathname)) document.title = 'AppShell';
    }, [pathname]);

    return (
        <Routes>
            {/* Public Routes (eagerly loaded) */}
            <Route path="/" element={<LandingPage />} />
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } 
            />
            
            {/* Public Share Page - Lazy loaded */}
            <Route
                path="/share/page/:pageId"
                element={
                    <Suspense fallback={<PublicPageLoadingFallback />}>
                        <PublicPageView />
                    </Suspense>
                }
            />

            {/* Lead Dashboard - Public prospect pages */}
            <Route
                path="/prospect/:slug"
                element={
                    <Suspense fallback={<PublicPageLoadingFallback />}>
                        <ProspectView />
                    </Suspense>
                }
            />

            {/* Lead Dashboard - the page editor's live preview (shown inside the editor) */}
            <Route
                path="/prospect-preview"
                element={
                    <Suspense fallback={<PublicPageLoadingFallback />}>
                        <ProspectPreviewView />
                    </Suspense>
                }
            />

            {/* Video Tracker - Public watch page */}
            <Route
                path="/watch/:shareToken"
                element={
                    <Suspense fallback={<PublicPageLoadingFallback />}>
                        <WatchVideo />
                    </Suspense>
                }
            />

            {/* Protected Routes - Shell Layout */}
            <Route
                element={
                    <ProtectedRoute>
                        <ShellLayout />
                    </ProtectedRoute>
                }
            >
                {/* Dashboard - Lazy loaded */}
                <Route
                    path="/dashboard"
                    element={
                        <Suspense fallback={<AppLoadingFallback appName="dashboard" />}>
                            <DashboardPage />
                        </Suspense>
                    }
                />
                <Route
                    path="/user-management"
                    element={
                        <AdminRoute>
                            <Suspense fallback={<AppLoadingFallback appName="user management" />}>
                                <UserManagementPage />
                            </Suspense>
                        </AdminRoute>
                    }
                />
                
                {/* App Modules - All lazy loaded */}
                {Object.keys(LazyApps).map((appId) => (
                    <Route 
                        key={appId}
                        path={`/apps/${appId}/*`} 
                        element={<LazyAppRoute appId={appId} />} 
                    />
                ))}
                
                {/* External Apps - renders iframe for any registered external app */}
                {Object.entries(EXTERNAL_APPS).map(([key, app]) => (
                    <Route 
                        key={key}
                        path={`/apps/${key}/*`} 
                        element={<ExternalApp appUrl={app.url} appName={app.name} />} 
                    />
                ))}
                
                {/* Generic App - catches all other app routes (for new/imported apps) */}
                <Route path="/apps/:appId/*" element={<GenericApp />} />
            </Route>

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// =============================================================================
// APP ROOT
// =============================================================================

function App() {
    return (
        <QueryClientProvider client={queryClient}>
        <BrowserRouter>
            <AuthProvider>
                {isBrandedHost() ? <BrandedProspectRoutes /> : <AppRoutes />}
                <Toaster
                    position="bottom-right"
                    richColors 
                    closeButton
                    toastOptions={{
                        style: {
                            fontFamily: 'Inter, sans-serif'
                        }
                    }}
                />
            </AuthProvider>
        </BrowserRouter>
        </QueryClientProvider>
    );
}

export default App;
